import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "next/navigation";
import { toast } from "sonner";
import useCart from "@/cart/useCart";
import useLayoutStore from "@/stores/useLayoutStore";
import ReferralToast from "./ReferralToast";
import { looksLikeReferralCode, resolveReferral } from "@/cart/referralCode";

const GLOBAL_TOASTER_ID = "global";
const REFERRAL_ERROR_MESSAGE =
  "We couldn't confirm your referral reward. Please try Order Now again.";

function ReferralToastTrigger() {
  const searchParams = useSearchParams();
  const toastIdRef = useRef<string | number | null>(null);
  const isPreparingRef = useRef(false);
  const isSubmittingRef = useRef(false);
  const openSideCart = useLayoutStore((state) => state.openSideCart);
  const isSideCartOpen = useLayoutStore((state) => state.isSideCartOpen);
  const isMenuOpen = useLayoutStore((state) => state.isMenuOpen);
  const isSweepstakesOpen = useLayoutStore((state) => state.isSweepstakesOpen);
  const ensureReferralCode = useCart((state) => state.ensureReferralCode);
  const addMatic = useCart((state) => state.addMatic);

  const clearReferralToast = useCallback((toastId: string | number) => {
    if (toastIdRef.current === toastId) {
      toastIdRef.current = null;
    }
  }, []);

  const dismissReferralToast = useCallback(
    (toastId = toastIdRef.current) => {
      if (toastId === null) {
        return;
      }

      toast.dismiss(toastId);

      clearReferralToast(toastId);
    },
    [clearReferralToast],
  );

  const handleOrderNow = useCallback(
    async (toastId: string | number, referralCode: string) => {
      if (isSubmittingRef.current) {
        return;
      }

      isSubmittingRef.current = true;
      dismissReferralToast(toastId);

      try {
        await addMatic(
          {
            source: "other",
            buttonLocation: "custom-referral-toast-order-now",
          },
          1,
          { codeOverride: referralCode },
        );
        openSideCart("referral_toast");
      } catch (error) {
        console.error("Failed to add referral reward to cart:", error);
        toast.error(REFERRAL_ERROR_MESSAGE, { toasterId: GLOBAL_TOASTER_ID });
      } finally {
        isSubmittingRef.current = false;
      }
    },
    [addMatic, dismissReferralToast, openSideCart],
  );

  const showReferralToast = useCallback(
    (referralCode: string) => {
      if (toastIdRef.current !== null) {
        return;
      }

      const toastId = toast.custom(
        (id) => (
          <ReferralToast
            toastId={id}
            referralCode={referralCode}
            onClose={dismissReferralToast}
            onOrderNow={(nextToastId) =>
              handleOrderNow(nextToastId, referralCode)
            }
          />
        ),
        {
          toasterId: GLOBAL_TOASTER_ID,
          duration: Infinity,
          onDismiss: (dismissedToast) => {
            clearReferralToast(dismissedToast.id);
          },
          classNames: {
            toast: "!bg-transparent !border-none !shadow-none !p-0 w-[392px]",
          },
        },
      );

      toastIdRef.current = toastId;
    },
    [clearReferralToast, dismissReferralToast, handleOrderNow],
  );

  const prepareAndShowReferralToast = useCallback(
    async (code: string | null | undefined) => {
      const trimmedCode = code?.trim() ?? "";

      if (
        !looksLikeReferralCode(trimmedCode) ||
        toastIdRef.current !== null ||
        isPreparingRef.current
      ) {
        return;
      }

      isPreparingRef.current = true;

      try {
        const referral = await resolveReferral(trimmedCode);
        if (!referral) {
          return;
        }

        await ensureReferralCode(referral.code);

        // The toast promises a free bag pass, so a code that gives none is
        // applied to the cart without it.
        if (referral.reward === "none") {
          return;
        }

        const {
          isMenuOpen: menuOpen,
          isSideCartOpen: sideCartOpen,
          isSweepstakesOpen: sweepstakesOpen,
        } = useLayoutStore.getState();

        if (!menuOpen && !sideCartOpen && !sweepstakesOpen) {
          showReferralToast(referral.code);
        }
      } catch (error) {
        console.error("Failed to prepare referral reward:", error);
      } finally {
        isPreparingRef.current = false;
      }
    },
    [ensureReferralCode, showReferralToast],
  );

  useEffect(() => {
    prepareAndShowReferralToast(searchParams.get("utm_term"));
  }, [prepareAndShowReferralToast, searchParams]);

  useEffect(() => {
    const openHandler = (event: Event) => {
      const passedCode =
        event instanceof CustomEvent && typeof event.detail?.code === "string"
          ? event.detail.code
          : null;
      const fallbackCode =
        passedCode ||
        useCart.getState().referralCode ||
        searchParams.get("utm_term");

      prepareAndShowReferralToast(fallbackCode);
    };

    window.addEventListener("open-custom-referral-popup", openHandler);
    return () => {
      window.removeEventListener("open-custom-referral-popup", openHandler);
    };
  }, [prepareAndShowReferralToast, searchParams]);

  useEffect(() => {
    if ((isSideCartOpen || isMenuOpen) && toastIdRef.current !== null) {
      const toastId = toastIdRef.current;
      toastIdRef.current = null;
      // delay to avoid dismissing mid-render when sonner is updating
      setTimeout(() => toast.dismiss(toastId), 50);
    }
  }, [isMenuOpen, isSideCartOpen]);

  // The giveaway modal takes priority: hide the toast while it's open (it would
  // sit unclickable behind the overlay), then re-show it once the modal closes.
  // Only a toast the modal actually hid comes back — closing the modal must not
  // resurrect a toast from the cart's stored referral code alone.
  const hiddenBySweepstakesRef = useRef(false);
  useEffect(() => {
    if (isSweepstakesOpen) {
      if (toastIdRef.current !== null) {
        const toastId = toastIdRef.current;
        toastIdRef.current = null;
        hiddenBySweepstakesRef.current = true;
        setTimeout(() => toast.dismiss(toastId), 50);
      }
    } else if (hiddenBySweepstakesRef.current) {
      hiddenBySweepstakesRef.current = false;
      prepareAndShowReferralToast(
        useCart.getState().referralCode || searchParams.get("utm_term"),
      );
    }
  }, [isSweepstakesOpen, prepareAndShowReferralToast, searchParams]);

  return null;
}

export default ReferralToastTrigger;
